import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, map, Observable } from 'rxjs';
import { ApiService } from './api.service';
import { DigiResponse } from '@core/models/response/core';
import { PupilDuplicateGroup } from '@core/models/domain/pupil-duplicate.domain';
import { buildApiEndpoint, handleError } from '@core/utils/service';

/**
 * PupilDuplicateService (DIGI-1763)
 *
 * Backend calls for the school's possible duplicate pupils: the same child entered more than once,
 * usually by two households. Any staff member can list them (Home and Devices use them to stop
 * chasing copies of a pupil whose phone already works); only an admin can mark a group as not the
 * same child. The school is taken from the caller's token by the backend.
 */
@Injectable({
  providedIn: 'root'
})
export class PupilDuplicateService {
  API_URI: string;

  constructor(private http: HttpClient, private apiService: ApiService) {
    this.API_URI = this.apiService.getApiURL("UserService", "school-account");
  }

  loadGroups(): Observable<PupilDuplicateGroup[]> {
    return this.http
      .get<DigiResponse<PupilDuplicateGroup[]>>(buildApiEndpoint(this.API_URI, "pupil-duplicates"))
      .pipe(
        map((response) => {
          if (response?.statusCode?.status !== "OK") {
            throw response?.statusCode ?? new Error("Loading possible duplicates failed");
          }
          return Array.isArray(response.body) ? response.body : [];
        }),
        catchError((error) => handleError<PupilDuplicateGroup[]>("Loading possible duplicates", error))
      );
  }

  dismiss(groupId: string): Observable<string> {
    return this.http
      .post<DigiResponse<string>>(buildApiEndpoint(this.API_URI, "pupil-duplicates/dismiss"), { groupId })
      .pipe(
        map((response) => {
          if (response?.statusCode?.status !== "OK") {
            throw response?.statusCode ?? new Error("Marking the group failed");
          }
          return groupId;
        }),
        catchError((error) => handleError<string>("Marking possible duplicate", error))
      );
  }
}
